import { h } from 'preact'
import { useEffect, useState } from 'preact/compat'
import { useDispatch } from 'react-redux'
import PropTypes from 'prop-types'
import { useTranslations } from '../../../hooks/useTranslations'
import { transactionDetailsUpdate } from '../../../redux/slice/transactionDetailsSlice'
import ShowMore from '../ShowMore'
import {
  formatAsCurrency,
  formatDateWithOrdinal,
  isNumberBetweenTwoNumbers,
} from '../../../helpers/common'
import PaymentBreakdownItem from './PaymentBreakdownItem'
import './style.scss'

export const formatDateItem = (item = '') =>
  item.toString().length === 1 ? `0${item}` : item

const PaymentBreakdown = ({
  directDebit,
  culture,
  currency,
  directDebitDate,
  overrideAmount,
}) => {
  const l10n = useTranslations()
  const dispatch = useDispatch()

  const [paymentBreakdowns, setPaymentBreakdowns] = useState([])

  useEffect(() => {
    const formatAmount = formatAsCurrency(currency, culture)

    const currentSchedule = directDebit.schedules.find(({ startDay, endDay }) =>
      isNumberBetweenTwoNumbers(startDay, endDay, +directDebitDate),
    )

    if (!currentSchedule) {
      setPaymentBreakdowns([])
      return
    }
    const formattedDate = formatDateWithOrdinal(
      `${currentSchedule.year}-${formatDateItem(
        currentSchedule.month,
      )}-${formatDateItem(directDebitDate)}`,
      l10n,
    )

    const amount = overrideAmount
      ? formatAmount(overrideAmount / currentSchedule.count)
      : formatAmount(currentSchedule.amount)
    const payBreakdowns = [...Array(currentSchedule.count - 1)].map(
      (num, index) => ({
        headingText: l10n.t('Payment') + ` ${index + 1}`,
        subtitleText: formattedDate(index),
        amount,
      }),
    )

    dispatch(
      transactionDetailsUpdate({
        currentPaymentPlan: {
          count: currentSchedule.count,
          amount,
        },
      }),
    )

    window.parent?.postMessage?.(
      {
        type: 'MFEStoreDispatch',
        data: {
          actionType: 'transactionDetailsUpdate',
          message: {
            currentPaymentPlan: {
              count: currentSchedule.count,
              amount,
            },
          },
        },
      },
      '*',
    )

    // add first element
    payBreakdowns.unshift({
      headingText: l10n.t('Due Now'),
      amount: formatAmount(0),
    })

    // add last element
    payBreakdowns.push({
      headingText: l10n.t('Final payment'),
      subtitleText: formattedDate(currentSchedule.count - 1),
      amount: overrideAmount
        ? formatAmount(overrideAmount / currentSchedule.count)
        : formatAmount(currentSchedule.lastAmount),
    })

    setPaymentBreakdowns(payBreakdowns)
  }, [directDebitDate, overrideAmount])

  return paymentBreakdowns.length ? (
    <ul className="payment-breakdown">
      {paymentBreakdowns.map(
        ({ headingText, subtitleText, amount }, index) =>
          index < 3 && (
            <PaymentBreakdownItem
              {...{
                headingText,
                subtitleText,
                amount,
              }}
              data-testid={`breakdown-item-${index}`}
            />
          ),
      )}

      {paymentBreakdowns.length > 3 && (
        <ShowMore
          visibleLabelText={l10n.t('Show more')}
          hiddenLabelText={l10n.t('Show less')}
          showInMobile={false}
          data-testId="payment-breakdowns-show-more"
        >
          {paymentBreakdowns.map(
            ({ headingText, subtitleText, amount }, index) =>
              index >= 3 && (
                <PaymentBreakdownItem
                  {...{ headingText, subtitleText, amount }}
                  data-testid={`breakdown-item-${index}`}
                />
              ),
          )}
        </ShowMore>
      )}
    </ul>
  ) : (
    l10n.t('No available payment breakdowns for this date')
  )
}

PaymentBreakdown.propTypes = {
  directDebit: PropTypes.object,
  culture: PropTypes.string,
  currency: PropTypes.string,
  directDebitDate: PropTypes.string,
  overrideAmount: PropTypes.number,
}

export default PaymentBreakdown
